import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useVenue } from "@/components/shared/VenueContext";
import { hosysAiApi } from "@/api/hosysai";
import HosysAILayout from "@/components/hosysai/HosysAILayout";
import {
  ShieldCheck, Brain, AlertTriangle, GraduationCap, Users,
  ClipboardList, ArrowRight, TrendingUp, Sparkles,
} from "lucide-react";

const RISK_COLORS = {
  low: "bg-emerald-500",
  moderate: "bg-amber-500",
  review_required: "bg-orange-500",
  high: "bg-red-500",
};

const RISK_LABELS = {
  low: "Baix",
  moderate: "Moderat",
  review_required: "Requereix revisió",
  high: "Risc important",
};

function ScoreRing({ score }) {
  const pct = Math.max(0, Math.min(100, score));
  const circumference = 2 * Math.PI * 54;
  const strokeDashoffset = circumference - (pct / 100) * circumference;
  const color = pct >= 80 ? "#10b981" : pct >= 60 ? "#f59e0b" : pct >= 40 ? "#f97316" : "#ef4444";
  return (
    <div className="relative w-40 h-40" data-testid="hosysai-score-ring">
      <svg className="w-full h-full transform -rotate-90">
        <circle cx="80" cy="80" r="54" stroke="rgba(255,255,255,0.1)" strokeWidth="10" fill="none" />
        <circle
          cx="80" cy="80" r="54" stroke={color} strokeWidth="10" fill="none"
          strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
          strokeLinecap="round" className="transition-all duration-700"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <p className="text-4xl font-bold text-white tabular-nums">{pct}<span className="text-2xl text-slate-400">%</span></p>
        <p className="text-[10px] uppercase tracking-widest text-slate-400">AI Compliance</p>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, tone = "cyan", to }) {
  const tones = {
    cyan:    "from-cyan-400/20 to-cyan-500/5 border-cyan-400/30 text-cyan-300",
    fuchsia: "from-fuchsia-400/20 to-fuchsia-500/5 border-fuchsia-400/30 text-fuchsia-300",
    emerald: "from-emerald-400/20 to-emerald-500/5 border-emerald-400/30 text-emerald-300",
    amber:   "from-amber-400/20 to-amber-500/5 border-amber-400/30 text-amber-300",
  };
  const inner = (
    <div className={`p-4 rounded-2xl border bg-gradient-to-br ${tones[tone]} h-full`}>
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-5 h-5" />
        {to && <ArrowRight className="w-4 h-4 opacity-40 group-hover:opacity-100 transition-opacity" />}
      </div>
      <p className="text-3xl font-bold text-white tabular-nums">{value}</p>
      <p className="text-xs text-slate-400 uppercase tracking-wider mt-0.5">{label}</p>
      {sub && <p className="text-xs text-slate-300 mt-2">{sub}</p>}
    </div>
  );
  if (to) return <Link to={to} className="block group">{inner}</Link>;
  return inner;
}

export default function HosysAIDashboard() {
  const { currentVenue, loading: venueLoading } = useVenue();
  const venueId = currentVenue?.venue_id || currentVenue?.id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    try {
      const d = await hosysAiApi.dashboard(venueId);
      setData(d);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => { load(); }, [load]);

  return (
    <HosysAILayout>
      <div className="space-y-6" data-testid="hosysai-dashboard-page">
        <div>
          <p className="text-xs uppercase tracking-widest text-cyan-300 font-semibold">Dashboard</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mt-1">Governança de la IA</h1>
          <p className="text-slate-400 text-sm mt-1">Estat del compliment normatiu, formació i sistemes d'IA de la teva organització.</p>
        </div>

        {venueLoading || loading || !data ? (
          <div className="h-64 flex items-center justify-center text-slate-400">Carregant…</div>
        ) : (
          <>
            {/* Score + main KPIs */}
            <div className="grid lg:grid-cols-4 gap-4">
              <div className="lg:col-span-1 p-5 rounded-2xl border border-white/10 bg-white/5 flex flex-col items-center justify-center">
                <ScoreRing score={data.compliance_score} />
                <p className="text-xs text-slate-400 mt-3 text-center max-w-[180px]">
                  Índex orientatiu de compliment en governança d'IA
                </p>
              </div>
              <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-3 gap-3">
                <KpiCard icon={Users} label="Treballadors" value={data.personnel.total} tone="cyan"
                  sub={`Formats: ${data.personnel.trained} / Pendents: ${data.personnel.pending}`} />
                <KpiCard icon={Brain} label="Sistemes IA" value={data.systems.total} tone="fuchsia"
                  sub={`${data.systems.by_risk.high + data.systems.by_risk.review_required} amb risc alt/revisió`}
                  to="/HosysAI/Systems" />
                <KpiCard icon={GraduationCap} label="Formacions completes" value={data.training.completed} tone="emerald" sub="Fase 1B — properament" />
                <KpiCard icon={AlertTriangle} label="Incidències obertes" value={data.incidents.open} tone="amber"
                  to="/HosysAI/Incidents" sub={`Total: ${data.incidents.total}`} />
                <KpiCard icon={ShieldCheck} label="Política IA activa" value={data.policy.active ? "Sí" : "No"} tone="cyan" sub="Fase 1C — properament" />
                <KpiCard icon={TrendingUp} label="Diagnòstic" value={data.diagnostic_completed ? "OK" : "Pendent"} tone={data.diagnostic_completed ? "emerald" : "amber"} to="/HosysAI/Diagnostic" />
              </div>
            </div>

            {/* Risk breakdown */}
            <div className="p-5 rounded-2xl border border-white/10 bg-white/5">
              <div className="flex items-center justify-between mb-3">
                <p className="font-semibold text-white flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-cyan-300" />
                  Sistemes IA per nivell de risc
                </p>
                <Link to="/HosysAI/Systems" className="text-xs text-cyan-300 hover:text-cyan-200 font-medium">Veure inventari →</Link>
              </div>
              {data.systems.total === 0 ? (
                <p className="text-slate-500 text-sm italic py-4">Encara no hi ha sistemes registrats.</p>
              ) : (
                <div className="space-y-2">
                  {["low", "moderate", "review_required", "high"].map((k) => (
                    <div key={k} className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${RISK_COLORS[k]}`} />
                      <span className="text-sm text-slate-300 w-40">{RISK_LABELS[k]}</span>
                      <div className="flex-1 bg-white/5 rounded-full h-2 overflow-hidden">
                        <div className={`h-full ${RISK_COLORS[k]}`}
                          style={{ width: `${(data.systems.by_risk[k] / Math.max(data.systems.total, 1)) * 100}%` }} />
                      </div>
                      <span className="text-sm font-semibold text-white w-10 text-right tabular-nums">{data.systems.by_risk[k]}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Alerts */}
            {data.alerts?.length > 0 && (
              <div className="p-5 rounded-2xl border border-amber-400/30 bg-amber-500/5">
                <p className="font-semibold text-white flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  Alertes actives ({data.alerts.length})
                </p>
                <ul className="space-y-2">
                  {data.alerts.map((a, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-200" data-testid={`alert-${a.type}`}>
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-2 ${
                        a.severity === "warning" ? "bg-amber-400" : "bg-cyan-400"
                      }`} />
                      <span className="flex-1">{a.message}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* CTA */}
            {!data.diagnostic_completed && (
              <Link to="/HosysAI/Diagnostic"
                className="block p-6 rounded-2xl bg-gradient-to-br from-cyan-500/20 via-fuchsia-500/20 to-purple-500/20 border border-cyan-400/30 hover:border-cyan-400/60 transition-all"
                data-testid="hosysai-cta-diagnostic">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-white text-lg">Comença amb el diagnòstic inicial</p>
                    <p className="text-sm text-slate-300 mt-0.5">12 preguntes ràpides per configurar HOSYS AI segons la teva organització.</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-cyan-300" />
                </div>
              </Link>
            )}
          </>
        )}
      </div>
    </HosysAILayout>
  );
}
